import { useLazyQuery } from '@apollo/client';
import { useOutreachActivityVisible } from 'hooks/use-outreach-activity-visible';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  OutreachActivity,
  OutreachActivity_outreachActivity,
  OutreachActivityVariables,
} from 'types/apollo/OutreachActivity';
import { ACTIVITY_WINDOW_MS } from './activity-window';
import { OutreachActivityQuery } from './outreach-activity.graphql';
import { useNotificationStream } from './use-notification-stream';

interface OutreachActivityContextValue {
  /** Newest-first. Consumers rely on this rather than re-sorting. */
  events: OutreachActivity_outreachActivity[];
  /** Newest createdAt among events with this refId, or null. Drives thread refetch. */
  latestForRef: (refId: string) => string | null;
  /**
   * `latestForRef` across several refs at once, for a surface whose content spans more than one.
   *
   * A comment thread is the case that needs it: a comment's refId is its PROPOSAL id when it is
   * proposal-scoped and the THREAD id when it isn't, yet one rail renders both — so watching the
   * thread id alone misses proposal-scoped arrivals. Nulls are ignored, and all-null yields null.
   */
  latestForRefs: (refIds: (string | null | undefined)[]) => string | null;
}

const POLL_INTERVAL_MS = 15000;

/**
 * How far back each poll re-asks, beyond the newest event already seen.
 *
 * The server's `since` is strictly exclusive (`created_at > $1`), so advancing the cursor to exactly
 * the newest `createdAt` would permanently skip any event sharing that timestamp that wasn't
 * committed yet when the poll ran. Overlapping by a few seconds and deduping by id is the standard
 * trade: a handful of already-seen rows per poll instead of a silently-lost event.
 */
const CURSOR_OVERLAP_MS = 5000;

const OutreachActivityContext = createContext<OutreachActivityContextValue>({
  events: [],
  latestForRef: () => null,
  latestForRefs: () => null,
});

export const OutreachActivityProvider: FC<{ children: ReactNode }> = ({ children }) => {
  // Same gate as the notification bell. This provider wraps the whole router, so without it an
  // excluded user still polls this (expensive) feed and holds an SSE connection for no bell.
  const enabled = useOutreachActivityVisible();

  // The first poll's `since`, and the floor the moving cursor can't go below. Pruning uses a fresh
  // cutoff instead, so the retained window slides rather than only growing.
  const windowStartRef = useRef<string>(new Date(Date.now() - ACTIVITY_WINDOW_MS).toISOString());
  // Max createdAt ever seen. The cursor is derived from it per pull rather than stored alongside it,
  // so there's no second value to drift.
  const newestSeenRef = useRef<string | null>(null);
  // The cursor moves, so each response is only a delta — the full window exists only here.
  const byIdRef = useRef(new Map<string, OutreachActivity_outreachActivity>());
  const [events, setEvents] = useState<OutreachActivity_outreachActivity[]>([]);

  // no-cache, not network-only: nothing reads this from the cache, so normalizing 500 rows per poll
  // would re-diff every watched query on the page for nothing.
  const [runQuery] = useLazyQuery<OutreachActivity, OutreachActivityVariables>(
    OutreachActivityQuery,
    { fetchPolicy: 'no-cache' },
  );

  const pull = useCallback(async () => {
    // Advances with the feed; a fixed cursor re-transfers the whole 30-day window on every poll.
    const newestSeen = newestSeenRef.current;
    const since = newestSeen
      ? new Date(
          Math.max(Date.parse(newestSeen) - CURSOR_OVERLAP_MS, Date.parse(windowStartRef.current)),
        ).toISOString()
      : windowStartRef.current;

    const { data } = await runQuery({ variables: { since } });
    const incoming = data?.outreachActivity ?? [];
    const byId = byIdRef.current;
    let changed = false;

    for (const e of incoming) {
      // Same id always means same content — the server embeds the timestamp in it (ACTIVITY_SQL) —
      // so re-seen rows from the overlap window cost nothing.
      if (!byId.has(e.id)) changed = true;
      byId.set(e.id, e);
      if (newestSeenRef.current === null || e.createdAt > newestSeenRef.current) {
        newestSeenRef.current = e.createdAt;
      }
    }

    // Drop what the server would no longer return, so a long-lived tab's bell matches the window.
    const cutoff = new Date(Date.now() - ACTIVITY_WINDOW_MS).toISOString();
    for (const [id, e] of byId) {
      if (e.createdAt < cutoff) {
        byId.delete(id);
        changed = true;
      }
    }

    if (changed) {
      setEvents([...byId.values()].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)));
    }
  }, [runQuery]);

  // While the SSE stream is connected the poll is off and pushed events drive the pull; polling is
  // the fallback whenever the stream never connects or drops.
  const [sseConnected, setSseConnected] = useState(false);

  // Reached through a ref so the SSE effect can have an empty dep list: otherwise the connection's
  // lifetime hangs on an Apollo callback's identity, and onOpenChange sets state — one reconnect loop
  // away from an Apollo release destabilising `runQuery`.
  const pullRef = useRef(pull);
  useEffect(() => {
    pullRef.current = pull;
  }, [pull]);

  // Fire-and-forget at three call sites, so each needs a rejection sink. Apollo's execute resolves
  // rather than rejects today; the feed is best-effort and the next tick retries either way.
  const safePull = useCallback(() => {
    pullRef.current().catch(() => undefined);
  }, []);

  const onOpenChange = useCallback(
    (open: boolean) => {
      setSseConnected(open);
      if (open) safePull(); // catch up on anything missed while disconnected
    },
    [safePull],
  );
  useNotificationStream({ onActivity: safePull, onOpenChange, enabled });

  useEffect(() => {
    if (!enabled) return;
    safePull();
  }, [enabled, safePull]);

  useEffect(() => {
    if (!enabled || sseConnected) return;
    const id = setInterval(safePull, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [enabled, sseConnected, safePull]);

  const value = useMemo<OutreachActivityContextValue>(() => {
    // Precomputed so latestForRef is O(1) rather than a scan per call.
    const latestByRef = new Map<string, string>();
    for (const e of events) {
      const cur = latestByRef.get(e.refId);
      if (cur === undefined || e.createdAt > cur) latestByRef.set(e.refId, e.createdAt);
    }
    const latestForRefs = (refIds: (string | null | undefined)[]): string | null => {
      let newest: string | null = null;
      for (const refId of refIds) {
        if (!refId) continue;
        const at = latestByRef.get(refId);
        if (at !== undefined && (newest === null || at > newest)) newest = at;
      }
      return newest;
    };
    return { events, latestForRef: (refId) => latestForRefs([refId]), latestForRefs };
  }, [events]);

  return (
    <OutreachActivityContext.Provider value={value}>{children}</OutreachActivityContext.Provider>
  );
};

export const useOutreachActivity = (): OutreachActivityContextValue =>
  useContext(OutreachActivityContext);
